import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, getFilteredRowModel, flexRender, } from '@tanstack/react-table';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@cargomatic/ui';
import { StopInformation } from './StopInformation';
import { DraggableRowWrapper } from './components/SortableRow';
import { createColumns } from './columns';
export function ShipmentList({ shipments, isLoading, onReorder, onEditShipment, onDeleteShipment, onOpenAddHub, onEditEquipment, onSelectionChange, onGenerateDeliveryOrder, onSendDeliveryOrder, onOpenDeliveryOrder, }) {
    const [selectedShipments, setSelectedShipments] = useState([]);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [expandedEquipment, setExpandedEquipment] = useState(new Set());
    const [isMounted, setIsMounted] = useState(false);
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState({});
    const [rowSelection, setRowSelection] = useState({});
    const dragListenersMapRef = useRef(new Map());
    useEffect(() => {
        setIsMounted(true);
    }, []);
    const handleSelectShipment = (id, checked) => {
        setSelectedShipments((prev) => {
            const next = checked
                ? [...prev, id]
                : prev.filter((shipmentId) => shipmentId !== id);
            if (onSelectionChange)
                onSelectionChange(next);
            return next;
        });
    };
    const toggleExpand = (id) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const toggleEquipmentExpand = (id) => {
        setExpandedEquipment((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const handleExpandAll = () => {
        setExpandedRows(new Set(shipments.map((s) => s._id)));
    };
    const handleCollapseAll = () => {
        setExpandedRows(new Set());
    };
    const allExpanded = shipments.length > 0 && expandedRows.size === shipments.length;
    const columns = useMemo(() => createColumns({
        selectedShipments,
        expandedRows,
        expandedEquipment,
        isMounted,
        allExpanded,
        shipments,
        handleSelectShipment,
        toggleExpand,
        toggleEquipmentExpand,
        handleExpandAll,
        handleCollapseAll,
        onEditShipment,
        onDeleteShipment,
        onOpenAddHub,
        onEditEquipment,
        dragListenersMapRef,
        onGenerateDeliveryOrder,
        onSendDeliveryOrder,
        onOpenDeliveryOrder,
    }), [
        selectedShipments,
        expandedRows,
        expandedEquipment,
        isMounted,
        allExpanded,
        shipments,
        onEditShipment,
        onDeleteShipment,
        onOpenAddHub,
        onEditEquipment,
    ]);
    const table = useReactTable({
        data: shipments,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        initialState: {
            pagination: {
                pageSize: 25,
            },
        },
    });
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = shipments.findIndex((s) => s._id === active.id);
            const newIndex = shipments.findIndex((s) => s._id === over.id);
            const reorderedShipments = arrayMove(shipments, oldIndex, newIndex);
            onReorder(reorderedShipments.map((s) => s._id));
        }
    };
    return (React.createElement("div", { className: "overflow-x-auto" }, isMounted ? (React.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd },
        React.createElement(Table, null,
            React.createElement(TableHeader, null, table.getHeaderGroups().map((headerGroup) => (React.createElement(TableRow, { key: headerGroup.id }, headerGroup.headers.map((header) => (React.createElement(TableHead, { key: header.id, style: { width: header.getSize() } }, header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext())))))))),
            React.createElement(TableBody, null, isLoading ? (React.createElement(React.Fragment, null, Array.from({ length: 5 }).map((_, index) => (React.createElement(TableRow, { key: index }, columns.map((_, colIndex) => (React.createElement(TableCell, { key: colIndex },
                React.createElement(Skeleton, { className: "h-4 w-full" }))))))))) : (React.createElement(SortableContext, { items: table
                    .getRowModel()
                    .rows.map((row) => row.original._id), strategy: verticalListSortingStrategy }, table.getRowModel().rows.map((row) => {
                const shipmentId = row.original._id;
                const isExpanded = expandedRows.has(shipmentId);
                const shipment = row.original;
                return (React.createElement(React.Fragment, { key: shipmentId },
                    React.createElement(DraggableRowWrapper, { id: shipmentId, onListenersReady: (listeners) => {
                            dragListenersMapRef.current.set(shipmentId, listeners);
                        } }, row.getVisibleCells().map((cell) => (React.createElement(TableCell, { key: cell.id, className: "align-top" }, flexRender(cell.column.columnDef.cell, cell.getContext()))))),
                    isExpanded && (React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: columns.length, className: "bg-muted/30 p-0" },
                            React.createElement(StopInformation, { shipment: shipment }))))));
            }))))))) : (React.createElement(Table, null,
        React.createElement(TableHeader, null, table.getHeaderGroups().map((headerGroup) => (React.createElement(TableRow, { key: headerGroup.id }, headerGroup.headers.map((header) => (React.createElement(TableHead, { key: header.id }, header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())))))))),
        React.createElement(TableBody, null,
            React.createElement(TableRow, null,
                React.createElement(TableCell, { colSpan: columns.length, className: "h-24 text-center" }, "Loading...")))))));
}
